import { useMotionValue, useTransform, animate, MotionValue } from 'framer-motion';
import { useMemo, useCallback } from 'react';
import { useAnalytics } from '../contexts/AnalyticsContext';

export interface SwipeAction {
  type: 'like' | 'dismiss';
  eventId: string;
  seriesId?: string;
  organizerId?: string;
  context: 'carte' | 'modale' | 'carte_preview';
}

interface DragProps {
  drag: 'x';
  dragConstraints: { left: number; right: number };
  onDragEnd: (event: any, info: { offset: { x: number }; velocity: { x: number } }) => Promise<void>;
}

interface Style {
  x: MotionValue<number>;
  rotate: MotionValue<number>;
}

interface SwipeCardReturn {
  dragProps: DragProps;
  style: Style;
  indicators: {
    likeScale: MotionValue<number>;
    dismissScale: MotionValue<number>;
    likeOpacity: MotionValue<number>;
    dismissOpacity: MotionValue<number>;
  };

  triggerSwipeRight: () => Promise<void>;
  triggerSwipeLeft: () => Promise<void>;
}

const SWIPE_THRESHOLD = 125; // Distance minimale pour déclencher le swipe
const VELOCITY_THRESHOLD = 500; // Vélocité minimale pour un swipe rapide
const ANIMATION_DURATION = 0.5; // Durée de l'animation en secondes

// ✅ SIMPLIFIÉ : Hook d'animation pur sans logique métier
export const useSwipeCard = (
  eventId: string,
  onLike: () => void,
  onDismiss: () => void,
  seriesId?: string,
  fullEvent?: any, // Garde pour compatibilité mais pas utilisé
  organizerId?: string
): SwipeCardReturn => {
  const analytics = useAnalytics();
  
  // Fonction de détection du contexte de swipe
  const detectSwipeContext = (): 'carte' | 'modale' | 'carte_preview' => {
    const modal = document.querySelector('.event-modal');
    const previewCard = document.querySelector('.preview-card-map');
    
    if (modal) return 'modale';
    if (previewCard) return 'carte_preview';
    return 'carte'; // Fallback pour la carte
  };

  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 0, 200], [-10, 0, 10]);
  const likeScale = useTransform(x, [0, SWIPE_THRESHOLD], [0.8, 1]);
  const dismissScale = useTransform(x, [-SWIPE_THRESHOLD, 0], [1, 0.8]);
  const likeOpacity = useTransform(x, [0, SWIPE_THRESHOLD], [0, 1]);
  const dismissOpacity = useTransform(x, [-SWIPE_THRESHOLD, 0], [1, 0]);

  // 🚀 OPTIMISATION : Mémoriser handleDragEnd pour éviter re-renders
  const handleDragEnd = useCallback(async (_: any, info: { offset: { x: number }; velocity: { x: number } }) => {
    const { offset, velocity } = info;
    const swipeThreshold = SWIPE_THRESHOLD;
    const swipeVelocityThreshold = VELOCITY_THRESHOLD;

    try {
      // Swipe à droite (like)
      if (offset.x > swipeThreshold || velocity.x > swipeVelocityThreshold) {
        // Animation sortie vers la droite
        await animate(x, window.innerWidth * 1.5, { 
          duration: ANIMATION_DURATION,
          onComplete: () => {
            // ✅ NOUVEAU : Analytics de swipe
            const context = detectSwipeContext();
            analytics.trackSwipeAction(
              { id: eventId, organizerId: organizerId || '' },
              'swipe_droite',
              context
            );
            
            // ✅ CALLBACK et reset
            onLike();
            
            // ✅ CORRECTION : Ne PAS remettre à zéro car la carte va disparaître de la liste
            // La carte doit rester hors écran pour éviter le "flash" de réapparition
            // setTimeout(() => {
            //   x.set(0);
            // }, 100);
          }
        });
        
        // Swipe à gauche (dismiss)
      } else if (offset.x < -swipeThreshold || velocity.x < -swipeVelocityThreshold) {
        // Animation sortie vers la gauche
        await animate(x, -window.innerWidth * 1.5, { 
          duration: ANIMATION_DURATION,
          onComplete: () => {
            // ✅ NOUVEAU : Analytics de swipe
            const context = detectSwipeContext();
            analytics.trackEventDismiss(
              { id: eventId, organizerId: organizerId || '' },
              context
            );
            
            // ✅ CALLBACK et reset
            onDismiss();
            
            // ✅ CORRECTION : Ne PAS remettre à zéro car la carte va disparaître de la liste
            // La carte doit rester hors écran pour éviter le "flash" de réapparition
            // setTimeout(() => {
            //   x.set(0);
            // }, 100);
          }
        });
        
        // Retour élastique
      } else {
        await animate(x, 0, { duration: ANIMATION_DURATION });
      }
    } catch (error) {
      console.error('❌ useSwipeCard - Erreur handleDragEnd:', error);
      // Retour élastique en cas d'erreur
      await animate(x, 0, { duration: ANIMATION_DURATION });
    }
  }, [x, onLike, onDismiss, eventId, seriesId, organizerId, analytics]);

  // ✅ SIMPLIFIÉ : Fonctions de swipe programmatique
  const triggerSwipeRight = async () => {
    try {
      await animate(x, window.innerWidth * 1.5, { 
        duration: ANIMATION_DURATION,
        onComplete: () => {
          // ✅ NOUVEAU : Analytics de swipe programmatique
          const context = detectSwipeContext();
          analytics.trackSwipeAction(
            { id: eventId, organizerId: organizerId || '' },
            'swipe_droite',
            context
          );
          
          // ✅ SIMPLIFIÉ : Utiliser directement le callback
          onLike();
          
          // ✅ CORRECTION : Ne PAS remettre à zéro pour les swipes programmatiques
          // setTimeout(() => {
          //   x.set(0);
          // }, 100);
        }
      });
    } catch (error) {
      console.error('❌ useSwipeCard - Erreur triggerSwipeRight:', error);
    }
  };

  const triggerSwipeLeft = async () => {
    try {
      await animate(x, -window.innerWidth * 1.5, { 
        duration: ANIMATION_DURATION,
        onComplete: () => {
          // ✅ NOUVEAU : Analytics de swipe programmatique
          const context = detectSwipeContext();
          analytics.trackEventDismiss(
            { id: eventId, organizerId: organizerId || '' },
            context
          );
          
          // ✅ SIMPLIFIÉ : Utiliser directement le callback
          onDismiss();
          
          // ✅ CORRECTION : Ne PAS remettre à zéro pour les swipes programmatiques
          // setTimeout(() => {
          //   x.set(0);
          // }, 100);
        }
      });
    } catch (error) {
      console.error('❌ useSwipeCard - Erreur triggerSwipeLeft:', error);
    }
  };

  // 🚀 OPTIMISATION : Mémoriser dragProps pour éviter re-renders
  const dragProps = useMemo(() => ({
    drag: 'x' as const,
    dragConstraints: { left: 0, right: 0 },
    onDragEnd: handleDragEnd,
  }), [handleDragEnd]);

  // 🚀 OPTIMISATION : Mémoriser style pour éviter re-renders
  const style = useMemo(() => ({
    x,
    rotate,
  }), [x, rotate]);

  // 🚀 OPTIMISATION : Mémoriser indicators pour éviter re-renders
  const indicators = useMemo(() => ({
    likeScale,
    dismissScale,
    likeOpacity,
    dismissOpacity,
  }), [likeScale, dismissScale, likeOpacity, dismissOpacity]);

  return {
    dragProps,
    style,
    indicators,
    triggerSwipeRight,
    triggerSwipeLeft,
  };
}; 